/** P10/P13/P24–P27 — world, pathfinding, combat, economy, fog, AI. */

import {
  DT,
  DISSOLVE_DUR,
  STAIN_DUR,
  COMBAT_BAR_DUR,
  Heap,
  Kind,
  MAP,
  MAX_ENTS,
  Ord,
  Spatial,
  Tile,
  clamp,
  dist2,
  hash2,
  makeEnt,
  mulberry32,
  dir8,
  tileAt,
  MAX_SPARKS,
} from './engine';
import type { Bolt, Civ, Ent, Spark, TeamEco } from './engine';
import type { TechPathId } from './content';
import type { Difficulty } from './match-config';
import {
  BUILD_HP_START,
  CIV_PROFILE,
  GATHER_MAX,
  POP_HALL,
  POP_HOUSE,
  STATS,
  gateOpen,
  isBuilding,
  isUnit,
  pathEffects,
  pathsForCiv,
} from './content';
import {
  OPENING_CENTER,
  OPENING_CAMP,
  OPENING_CONVOY,
  OPENING_CORRIDOR,
  OPENING_MESA,
  OPENING_VENTS,
  inOpeningCamp,
  inOpeningCorridor,
} from './opening-presentation';
import { SEEN_PLAYER, SEEN_RIVAL, makeHeliosLandmarks } from './discovery';
import type { DiscoveryEvent, EntityDiscoveryEvent, Landmark } from './discovery';

/** M3-A — Sunweaver Solar collection link record (docs/M3_ECONOMIES.md). */
export interface Link {
  nodeId: number;
  hallId: number;
  team: number;
  /** Tick until which the link is severed; -1 = live. */
  severedUntil: number;
}

/** VS-2B — read-only snapshot of Central Lumen Field ownership. */
export interface LumenState {
  owner: -1 | 0 | 1;
  capturing: -1 | 0 | 1;
  progress: number;
  contested: boolean;
  pulseRemaining: readonly [number, number];
}

export interface MatchTeamStats {
  resources: { ore: number; gas: number; energy: number };
  unitsTrained: number;
  unitsLost: number;
  coreDamage: number;
}

export interface MatchStats {
  tick: number;
  teams: readonly [MatchTeamStats, MatchTeamStats];
}

const DX = [1, -1, 0, 0, 1, 1, -1, -1];
const DZ = [0, 0, 1, -1, 1, -1, 1, -1];
const DC = [1, 1, 1, 1, 1.4142, 1.4142, 1.4142, 1.4142];
/** Scripted marshal wave trains faster off-screen (DESIGN §6). */
const MARSHAL_FIGHTER_TRAIN = 1;
const MARSHAL_PEEL_STAGGER_TICKS = 3;
/** Enemy forward pad — marshal rally (off opening crop). */
const MARSHAL_FORWARD_X = MAP - 18;
const MARSHAL_FORWARD_Z = MAP - 16;
/** M4 decision 2 — Nexus path commit cost and channel seconds. */
const PATH_COMMIT_ORE = 400;
const PATH_COMMIT_CHARGE = 80;
const PATH_COMMIT_CHANNEL = 40;
export const OPENING_ORE_RESERVE = 700;
/** VS-2B — central objective contract. */
const LUMEN_CAPTURE_RADIUS = 4.5;
const LUMEN_CAPTURE_SECONDS = 5;
const LUMEN_PULSE_SECONDS = 4;
const LUMEN_PULSE_INTERVAL = 30;
/** M3-A base Solar link re-form delay after a sever, in ticks; solar-ascendancy halves it. */
const LINK_SEVER_TICKS = 300;
/** VS-2A — AI reaction cadence changes only with the selected difficulty. */
const AI_CADENCE_SECONDS: Record<Difficulty, number> = {
  cadet: 2.6,
  standard: 1.4,
  veteran: 0.8,
};
/** VS-2A-R2 — the rival may legally commit its technology path from 4:30. */
const AI_PATH_EARLIEST_TICK = Math.round((4.5 * 60) / DT);
/** VS-2A-R2 — the rival may target the discovered player Core from 8:00. */
const AI_ATTACK_EARLIEST_TICK = Math.round((8 * 60) / DT);
/** VS-2A — deterministic Yard ring, tested in array order. */
const AI_YARD_RING_OFFSETS = [
  { dx: -3.4, dz: -3.4 },
  { dx: 0, dz: -4.4 },
  { dx: 3.4, dz: -3.4 },
  { dx: 4.4, dz: 0 },
  { dx: 3.4, dz: 3.4 },
  { dx: 0, dz: 4.4 },
  { dx: -3.4, dz: 3.4 },
  { dx: -4.4, dz: 0 },
] as const;
/** VS-2A — one honest housing pad for the four-unit compact force. */
const AI_HOUSE_RING_OFFSETS = [
  { dx: 3.4, dz: -3.4 },
  { dx: -3.4, dz: 3.4 },
  { dx: 0, dz: -4.4 },
  { dx: 4.4, dz: 0 },
  { dx: 0, dz: 4.4 },
  { dx: -4.4, dz: 0 },
  { dx: -3.4, dz: -3.4 },
  { dx: 3.4, dz: 3.4 },
] as const;

export class World {
  readonly ents: Ent[] = Array.from({ length: MAX_ENTS }, makeEnt);
  readonly tiles = new Uint8Array(MAP * MAP);
  /** P96 — terraced elevation levels 0–3 per tile. */
  readonly height = new Uint8Array(MAP * MAP);
  readonly block = new Uint8Array(MAP * MAP);
  readonly explored = [new Uint8Array(MAP * MAP), new Uint8Array(MAP * MAP)];
  readonly visible = [new Uint8Array(MAP * MAP), new Uint8Array(MAP * MAP)];
  readonly teams: TeamEco[] = [
    { ore: 220, gas: 40, energy: 90, pop: 0, cap: 0, epoch: 0, ageT: 0, techPath: null },
    { ore: 220, gas: 40, energy: 90, pop: 0, cap: 0, epoch: 0, ageT: 0, techPath: null },
    { ore: 0, gas: 0, energy: 0, pop: 0, cap: 0, epoch: 0, ageT: 0, techPath: null },
    { ore: 0, gas: 0, energy: 0, pop: 0, cap: 0, epoch: 0, ageT: 0, techPath: null },
  ];
  readonly civ: Civ[] = ['vespari', 'aurion', 'voidmarked', 'vespari'];
  /** Gameplay fog by default; pass `?fog=0` from main for clear-map review. */
  fogOfWarEnabled = true;
  /** M2-D — legacy scripted marshal cheats are off by default (docs/M2_D_AI_KNOWLEDGE.md). */
  scriptedMarshalEnabled = false;
  /** VS-2A — public difficulty input; it changes cadence only. */
  aiDifficulty: Difficulty = 'standard';
  /** M3-A — Sunweaver Solar collection links (docs/M3_ECONOMIES.md). */
  readonly links: Link[] = [];
  /** M3-C — per-team active boost: 0 off · 1 production · 2 vision · 3 shields. */
  readonly boosts: number[] = [0, 0];
  /** M4 — path chosen for the running commit channel; applied when ageT hits 0. */
  private pendingPath: (TechPathId | null)[] = [null, null, null, null];
  readonly bolts: Bolt[] = [];
  readonly sparks: Spark[] = [];
  readonly flags: { x: number; z: number; t: number }[] = [];
  readonly hash = new Spatial();
  readonly q: number[] = [];
  /** M2-B — Helios Rift landmarks; fresh records with cleared latches on every reset. */
  landmarks: Landmark[] = makeHeliosLandmarks();
  /** M2-B — chronological discovery events (entities, resources, structures). */
  readonly discoveryLog: DiscoveryEvent[] = [];
  /** Wired from main; fires on first sight of an entity or landmark per team. */
  onDiscover?: (event: DiscoveryEvent) => void;
  tick = 0;
  seed = 0x5eed;
  /** -1 in play · 0 player win · 1 enemy win */
  winner = -1;
  /** P57 — combat SFX hooks (wired from main; sim stays renderer-free). */
  onHit?: () => void;
  onMuzzle?: () => void;
  private heap = new Heap();
  private gScore = new Float32Array(MAP * MAP);
  private came = new Int32Array(MAP * MAP);
  private closed = new Uint16Array(MAP * MAP);
  private stamp = 1;
  private free: number[] = [];
  private sporeT = [0, 0, 0, 0];
  private aiT = 0;
  private marshalPeelBuilt = false;
  private marshalPeelQ: number[] = [];
  private marshalPeelI = 0;
  private sparkHead = 0;
  private lumenOwner: -1 | 0 | 1 = -1;
  private lumenCapturing: -1 | 0 | 1 = -1;
  private lumenProgress = 0;
  private lumenContested = false;
  private lumenPulseRemaining: [number, number] = [0, 0];
  private lumenChargeAccum: [number, number] = [0, 0];
  private lumenOwnedAccum: [number, number] = [0, 0];
  private matchStatsData: [MatchTeamStats, MatchTeamStats] = [
    this.makeMatchTeamStats(),
    this.makeMatchTeamStats(),
  ];
  private matchStatsTracking = false;

  constructor() {
    for (let i = 0; i < MAX_ENTS; i++) {
      this.ents[i].id = i;
      this.free.push(i);
    }
    for (let i = 0; i < MAX_SPARKS; i++) {
      this.sparks.push({
        active: false,
        x: 0,
        z: 0,
        life: 0,
        maxLife: 0.1,
        kind: 0,
        civ: 'vespari',
      });
    }
  }

  private makeMatchTeamStats(): MatchTeamStats {
    return {
      resources: { ore: 0, gas: 0, energy: 0 },
      unitsTrained: 0,
      unitsLost: 0,
      coreDamage: 0,
    };
  }

  private resetMatchStats(): void {
    this.matchStatsData[0] = this.makeMatchTeamStats();
    this.matchStatsData[1] = this.makeMatchTeamStats();
  }

  private recordGathered(team: number, resource: 'ore' | 'gas' | 'energy', amount: number): void {
    if (!this.matchStatsTracking || (team !== 0 && team !== 1) || amount <= 0) return;
    this.matchStatsData[team].resources[resource] += amount;
  }

  reset(seed = 0x5eed): void {
    this.matchStatsTracking = false;
    this.resetMatchStats();
    this.seed = seed;
    this.tick = 0;
    this.winner = -1;
    this.resetLumen();
    this.bolts.length = 0;
    for (let i = 0; i < MAX_SPARKS; i++) this.sparks[i].active = false;
    this.sparkHead = 0;
    this.flags.length = 0;
    this.free.length = 0;
    for (let i = 0; i < MAX_ENTS; i++) {
      this.ents[i].alive = false;
      this.ents[i].id = i;
      this.free.push(i);
    }
    const playerStart = CIV_PROFILE[this.civ[0]];
    const enemyStart = CIV_PROFILE[this.civ[1]];
    this.teams[0] = {
      ore: playerStart.startOre,
      gas: playerStart.startGas,
      energy: playerStart.startEnergy,
      pop: 0,
      cap: 0,
      epoch: 0,
      ageT: 0,
      techPath: null,
    };
    this.teams[1] = {
      ore: enemyStart.startOre,
      gas: enemyStart.startGas,
      energy: enemyStart.startEnergy,
      pop: 0,
      cap: 0,
      epoch: 0,
      ageT: 0,
      techPath: null,
    };
    this.explored[0].fill(0);
    this.explored[1].fill(0);
    this.visible[0].fill(0);
    this.visible[1].fill(0);
    this.marshalPeelBuilt = false;
    this.marshalPeelQ = [];
    this.marshalPeelI = 0;
    this.aiT = 0;
    this.discoveryLog.length = 0;
    this.landmarks = makeHeliosLandmarks();
    this.links.length = 0;
    this.boosts[0] = 0;
    this.boosts[1] = 0;
    this.pendingPath = [null, null, null, null];
    this.genMap();
    this.spawnScenario();
    this.matchStatsTracking = true;
    this.recountPop();
    this.updateFog();
  }

  matchStats(): MatchStats {
    return {
      tick: this.tick,
      teams: [
        {
          resources: { ...this.matchStatsData[0].resources },
          unitsTrained: this.matchStatsData[0].unitsTrained,
          unitsLost: this.matchStatsData[0].unitsLost,
          coreDamage: this.matchStatsData[0].coreDamage,
        },
        {
          resources: { ...this.matchStatsData[1].resources },
          unitsTrained: this.matchStatsData[1].unitsTrained,
          unitsLost: this.matchStatsData[1].unitsLost,
          coreDamage: this.matchStatsData[1].coreDamage,
        },
      ],
    };
  }

  spawn(kind: Kind, civ: Civ, team: number, x: number, z: number): Ent | null {
    const id = this.free.pop();
    if (id === undefined) return null;
    const e = this.ents[id];
    const st = STATS[kind];
    e.alive = true;
    e.kind = kind;
    e.civ = civ;
    e.team = team;
    e.x = e.px = x;
    e.z = e.pz = z;
    e.vx = e.vz = 0;
    e.hp = st.hp;
    e.maxHp = st.hp;
    e.order = Ord.Idle;
    e.tx = x;
    e.tz = z;
    e.tid = -1;
    e.cargo = 0;
    e.cooldown = 0;
    e.anim = 0;
    e.facing = kind === Kind.Worker ? 6 : 1;
    e.stealth = kind === Kind.Shade ? 1 : 0;
    e.frenzy = 0;
    e.blinkCd = 0;
    e.buildKind = Kind.House;
    e.progress = 1;
    e.trainKind = Kind.Worker;
    e.trainT = 0;
    e.rallyX = x + (team === 0 ? 2.2 : -2.2);
    e.rallyZ = z;
    e.radius = st.radius;
    e.vis = true;
    e.seenBy = 0;
    e.rigTeam = -1;
    e.rigProgress = 0;
    e.rigHp = 0;
    e.rigAccum = 0;
    e.path = null;
    e.pathI = 0;
    e.hitFlash = 0;
    e.combatT = 0;
    e.dissolveT = 0;
    e.corpseT = 0;
    if (kind === Kind.Hall) e.hp = st.hp;
    if (this.matchStatsTracking && (team === 0 || team === 1) && isUnit(kind)) {
      this.matchStatsData[team].unitsTrained++;
    }
    return e;
  }

  private markCombat(...ents: Ent[]): void {
    for (const e of ents) {
      if (e.alive && e.hp > 0) e.combatT = COMBAT_BAR_DUR;
    }
  }

  kill(e: Ent): void {
    if (!e.alive) return;
    if (isUnit(e.kind)) {
      const firstDeath = e.corpseT <= 0 && e.dissolveT <= 0;
      e.hp = 0;
      e.dissolveT = DISSOLVE_DUR;
      e.corpseT = STAIN_DUR;
      e.vx = e.vz = 0;
      e.path = null;
      e.tid = -1;
      e.order = Ord.Idle;
      if (firstDeath && this.matchStatsTracking && (e.team === 0 || e.team === 1)) {
        this.matchStatsData[e.team].unitsLost++;
      }
      this.recountPop();
      return;
    }
    e.alive = false;
    e.order = Ord.Idle;
    this.free.push(e.id);
    this.recountPop();
    this.checkWinner();
  }

  private hasNexus(team: number): boolean {
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.team !== team || e.kind !== Kind.Hall) continue;
      if (e.hp > 0 && e.progress >= 1) return true;
    }
    return false;
  }

  checkWinner(): void {
    if (this.winner !== -1) return;
    const p0 = this.hasNexus(0);
    const p1 = this.hasNexus(1);
    if (!p0 && p1) this.winner = 1;
    else if (p0 && !p1) this.winner = 0;
  }

  /** Compact hex-ish slots around a move destination (AoE2-style formation spread). */
  private formationOffsets(n: number, spacing: number): { dx: number; dz: number }[] {
    if (n <= 1) return [{ dx: 0, dz: 0 }];
    const cols = Math.ceil(Math.sqrt(n));
    const rows = Math.ceil(n / cols);
    const out: { dx: number; dz: number }[] = [];
    const rowH = spacing * 0.866;
    let k = 0;
    for (let r = 0; r < rows && k < n; r++) {
      const inRow = Math.min(cols, n - k);
      const rowW = (inRow - 1) * spacing;
      const stagger = r % 2 === 1 ? spacing * 0.5 : 0;
      for (let c = 0; c < inRow; c++) {
        out.push({
          dx: c * spacing - rowW * 0.5 + stagger,
          dz: r * rowH - (rows - 1) * rowH * 0.5,
        });
        k++;
      }
    }
    return out;
  }

  issue(ids: number[], ord: Ord, x: number, z: number, tid: number): void {
    this.flags.push({ x, z, t: 0.9 });
    const spread = ids.length > 1 && (ord === Ord.Move || ord === Ord.AttackMove);
    const unitIds: number[] = [];
    for (const id of ids) {
      const e = this.ents[id];
      if (!e.alive || e.team !== 0) continue;
      if (isBuilding(e.kind)) {
        e.rallyX = x;
        e.rallyZ = z;
        continue;
      }
      unitIds.push(id);
    }
    let offsets: { dx: number; dz: number }[] | null = null;
    if (spread && unitIds.length > 1) {
      let maxR = 0.32;
      let hasWorker = false;
      for (const id of unitIds) {
        const kind = this.ents[id].kind;
        maxR = Math.max(maxR, STATS[kind].radius);
        hasWorker ||= kind === Kind.Worker;
      }
      // Worker sprites are wider than their collision radius. Give worker
      // groups a larger visual lane so the destination does not stack bodies.
      const spacing = hasWorker ? 1.25 : clamp(maxR * 2.6, 0.7, 1.1);
      offsets = this.formationOffsets(unitIds.length, spacing);
    }
    for (let i = 0; i < unitIds.length; i++) {
      const e = this.ents[unitIds[i]];
      const gx = offsets ? x + offsets[i].dx : x;
      const gz = offsets ? z + offsets[i].dz : z;
      e.order = ord;
      e.tx = gx;
      e.tz = gz;
      e.tid = tid;
      e.path = this.pathfind(e.x, e.z, gx, gz);
      e.pathI = 0;
      if (e.kind === Kind.Shade) e.stealth = 0;
    }
  }

  /** M4 decision 2/3 — begin the irreversible Nexus path commit; rejects leave no trace. */
  tryCommitPath(team: number, path: TechPathId): boolean {
    if (team < 0 || team >= this.teams.length) return false;
    if (!pathsForCiv(this.civ[team]).includes(path)) return false;
    const eco = this.teams[team];
    if (eco.epoch !== 0 || eco.ageT > 0 || eco.techPath !== null) return false;
    let hallReady = false;
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.team !== team || e.kind !== Kind.Hall) continue;
      if (e.trainT > 0) return false;
      if (e.hp > 0 && e.progress >= 1) hallReady = true;
    }
    if (!hallReady) return false;
    if (eco.ore < PATH_COMMIT_ORE || eco.energy < PATH_COMMIT_CHARGE) return false;
    eco.ore -= PATH_COMMIT_ORE;
    eco.energy -= PATH_COMMIT_CHARGE;
    eco.ageT = PATH_COMMIT_CHANNEL;
    this.pendingPath[team] = path;
    return true;
  }

  /** M4 — committed path of a team (null before the commit channel completes). */
  techPathOf(team: number): TechPathId | null {
    return this.teams[team]?.techPath ?? null;
  }

  /** M4 — path currently being committed by a team, before it locks in. */
  pendingPathOf(team: number): TechPathId | null {
    return this.pendingPath[team] ?? null;
  }

  /** M4 — seconds remaining on the active commit channel (0 when idle or done). */
  pathChannelT(team: number): number {
    return this.teams[team]?.ageT ?? 0;
  }

  /** VS-2B — return a defensive, read-only-through-method Lumen snapshot. */
  lumenState(): LumenState {
    return {
      owner: this.lumenOwner,
      capturing: this.lumenCapturing,
      progress: this.lumenProgress,
      contested: this.lumenContested,
      pulseRemaining: [this.lumenPulseRemaining[0], this.lumenPulseRemaining[1]],
    };
  }

  tryTrain(building: Ent, kind: Kind): boolean {
    if (kind === Kind.Siege || kind === Kind.Shade) return false;
    if (!building.alive || !isBuilding(building.kind)) return false;
    if (building.trainT > 0) return false;
    const st = STATS[kind];
    const eco = this.teams[building.team];
    if (building.kind === Kind.Hall && eco.ageT > 0) return false;
    if (building.kind === Kind.Barracks && !gateOpen(eco, kind)) return false;
    if (eco.ore < st.ore || eco.gas < st.gas || eco.energy < st.energy) return false;
    if (eco.pop + st.pop > eco.cap) return false;
    eco.ore -= st.ore;
    eco.gas -= st.gas;
    eco.energy -= st.energy;
    building.trainKind = kind;
    const trainMul = kind === Kind.Prism ? pathEffects(eco.techPath).siegeTrainMul : 1;
    building.trainT = st.train * trainMul;
    return true;
  }

  tryPlace(team: number, kind: Kind, x: number, z: number, builderId: number): boolean {
    const st = STATS[kind];
    const eco = this.teams[team];
    if (eco.ore < st.ore || eco.gas < st.gas || eco.energy < st.energy) return false;
    if (!this.canPlace(x, z, st.radius)) return false;
    const b = this.ents[builderId];
    if (!b?.alive || b.kind !== Kind.Worker) return false;
    eco.ore -= st.ore;
    eco.gas -= st.gas;
    eco.energy -= st.energy;
    const built = this.spawn(kind, this.civ[team], team, x, z);
    if (!built) return false;
    if (team === 1 && kind === Kind.Barracks) {
      built.rallyX = OPENING_CENTER.x;
      built.rallyZ = OPENING_CENTER.z;
    }
    built.progress = BUILD_HP_START;
    built.hp = Math.max(1, st.hp * BUILD_HP_START);
    b.order = Ord.Build;
    b.tid = built.id;
    b.tx = x;
    b.tz = z;
    b.path = this.pathfind(b.x, b.z, x, z);
    b.pathI = 0;
    return true;
  }

  canPlace(x: number, z: number, r: number): boolean {
    const x0 = clamp(Math.floor(x - r), 0, MAP - 1);
    const x1 = clamp(Math.floor(x + r), 0, MAP - 1);
    const z0 = clamp(Math.floor(z - r), 0, MAP - 1);
    const z1 = clamp(Math.floor(z + r), 0, MAP - 1);
    for (let zz = z0; zz <= z1; zz++) {
      for (let xx = x0; xx <= x1; xx++) {
        if (this.block[xx + zz * MAP]) return false;
      }
    }
    this.hash.query(x, z, r + 1.2, this.q);
    for (const id of this.q) {
      const o = this.ents[id];
      if (!o.alive || !isBuilding(o.kind)) continue;
      if (dist2(x, z, o.x, o.z) < (r + o.radius) * (r + o.radius) * 0.85) return false;
    }
    return true;
  }

  step(): void {
    if (this.winner !== -1) return;
    this.tick++;
    this.hash.clear();
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.hp <= 0) continue;
      e.px = e.x;
      e.pz = e.z;
      this.hash.insert(i, e.x, e.z);
    }
    this.thinkUnits();
    this.thinkBuildings();
    this.stepRigs();
    this.stepBoosts();
    this.stepAge();
    this.stepCorpses();
    this.moveSeparate();
    this.stepBolts();
    this.stepSparks();
    this.stepLumen();
    this.updateFog();
    this.stepFlags();
    if (this.scriptedMarshalEnabled) {
      this.stepEnemyMarshal();
      this.stepMarshalPeel();
    }
    this.stepAi();
    if ((this.tick & 7) === 0) this.recountPop();
    this.checkWinner();
  }

  private genMap(): void {
    const rng = mulberry32(this.seed);
    this.tiles.fill(Tile.Void);
    this.height.fill(0);
    this.block.fill(0);
    for (let z = 0; z < MAP; z++) {
      for (let x = 0; x < MAP; x++) {
        const n = hash2(x, z, this.seed);
        let t = Tile.Void;
        if (n > 0.46) t = Tile.Dust;
        this.tiles[x + z * MAP] = t;
      }
    }
    this.generateHeightmap(rng);
    this.stampMesa(
      OPENING_CENTER.x + OPENING_MESA.dx,
      OPENING_CENTER.z + OPENING_MESA.dz,
      OPENING_MESA.radius,
      OPENING_MESA.peak,
      0,
      0.18,
      1.0,
    );
    this.stampPatch(Tile.Ore, 9, rng);
    this.stampPatch(Tile.Gas, 6, rng);
    this.stampPatch(Tile.Solar, 5, rng);
    this.stampOpeningGround();
    this.clearBase(10, 10);
    this.clearBase(MAP - 11, MAP - 11);
    this.flattenHeightPads();
    this.skirtRampsAroundPads();
    this.applyHeightCliffs();
    if (!this.pathfind(10.5, 10.5, MAP - 10.5, MAP - 10.5)) {
      this.carveBentPath(10, 10, MAP - 11, MAP - 11, rng);
    }
  }

  /**
   * Playable RTS height: mostly plains, a few seeded mesas.
   * Positions/shapes come from the map RNG — not a diagonal mirror pair.
   */
  private generateHeightmap(rng: () => number): void {
    const placed: { x: number; z: number; r: number }[] = [];
    const want = 3 + ((rng() * 3) | 0);
    for (let attempt = 0; attempt < 90 && placed.length < want; attempt++) {
      const plateauR = 3.6 + rng() * 3.2;
      const peak = rng() < 0.38 ? 3 : 2;
      const margin = plateauR + 5;
      const cx = margin + rng() * (MAP - 2 * margin);
      const cz = margin + rng() * (MAP - 2 * margin);
      if (this.mesaBlocked(cx, cz, plateauR, placed)) continue;
      this.stampMesa(cx, cz, plateauR, peak, rng() * Math.PI * 2, rng() * Math.PI, 0.62 + rng() * 0.7);
      placed.push({ x: cx, z: cz, r: plateauR });
    }
  }

  private mesaBlocked(
    cx: number,
    cz: number,
    r: number,
    placed: { x: number; z: number; r: number }[],
  ): boolean {
    if (Math.hypot(cx - 10, cz - 10) < 13 + r) return true;
    if (Math.hypot(cx - (MAP - 11), cz - (MAP - 11)) < 13 + r) return true;
    if (Math.hypot(cx - OPENING_CENTER.x, cz - OPENING_CENTER.z) < 15 + r) return true;
    for (const p of placed) {
      if (Math.hypot(cx - p.x, cz - p.z) < p.r + r + 7) return true;
      // Don't place a diagonal reflection of an existing mesa.
      if (Math.hypot(cx - (MAP - 1 - p.x), cz - (MAP - 1 - p.z)) < p.r + r + 8) return true;
    }
    return false;
  }

  /** Elliptical mesa: walkable plateau, cliff rim, ramp wedge at a seeded angle. */
  private stampMesa(
    cx: number,
    cz: number,
    plateauR: number,
    peak: number,
    rampAng: number,
    rot: number,
    aspect: number,
  ): void {
    const rampR = plateauR + 3;
    const rampHalf = 0.55 + aspect * 0.2;
    const reach = rampR * Math.max(1, aspect) + 2;
    const x0 = Math.max(1, Math.floor(cx - reach));
    const x1 = Math.min(MAP - 2, Math.ceil(cx + reach));
    const z0 = Math.max(1, Math.floor(cz - reach));
    const z1 = Math.min(MAP - 2, Math.ceil(cz + reach));
    const cr = Math.cos(-rot);
    const sr = Math.sin(-rot);
    for (let z = z0; z <= z1; z++) {
      for (let x = x0; x <= x1; x++) {
        const dx = x + 0.5 - cx;
        const dz = z + 0.5 - cz;
        const lx = dx * cr - dz * sr;
        const lz = dx * sr + dz * cr;
        const d = Math.hypot(lx, lz / aspect);
        if (d > rampR) continue;
        const i = x + z * MAP;
        if (this.tiles[i] === Tile.Void) this.tiles[i] = Tile.Dust;
        const ang = Math.atan2(dz, dx);
        let dang = Math.abs(ang - rampAng);
        if (dang > Math.PI) dang = Math.PI * 2 - dang;
        const onRamp = dang < rampHalf;
        let level = 0;
        if (d <= plateauR) {
          const u = d / Math.max(1, plateauR);
          if (peak >= 3 && u < 0.5) level = 3;
          else level = Math.min(peak, 2);
        } else if (onRamp) {
          level = 1;
        }
        if (level > this.height[i]) this.height[i] = level;
      }
    }
  }

  /** Off-diagonal polyline so a wall of mesas cannot soft-lock the 1v1. */
  private carveBentPath(ax: number, az: number, bx: number, bz: number, rng: () => number): void {
    let wx = 10 + rng() * (MAP - 20);
    let wz = 10 + rng() * (MAP - 20);
    if (Math.abs(wx - wz) < 12) wz = clamp(wz + (rng() < 0.5 ? 16 : -16), 10, MAP - 11);
    this.carveSegment(ax, az, wx, wz);
    this.carveSegment(wx, wz, bx, bz);
  }

  private carveSegment(x0: number, z0: number, x1: number, z1: number): void {
    const steps = Math.max(1, (Math.hypot(x1 - x0, z1 - z0) * 2) | 0);
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const x = (x0 + (x1 - x0) * t) | 0;
      const z = (z0 + (z1 - z0) * t) | 0;
      for (let dz = -1; dz <= 1; dz++) {
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx;
          const zz = z + dz;
          if (xx < 1 || zz < 1 || xx >= MAP - 1 || zz >= MAP - 1) continue;
          const i = xx + zz * MAP;
          this.height[i] = 0;
          if (this.tiles[i] === Tile.Void || this.tiles[i] === Tile.Rock) this.tiles[i] = Tile.Dust;
          this.block[i] = 0;
        }
      }
    }
  }

  private isHeightPad(x: number, z: number): boolean {
    if (inOpeningCorridor(x + 0.5, z + 0.5) || inOpeningCamp(x + 0.5, z + 0.5)) return true;
    if (x >= 4 && x <= 16 && z >= 4 && z <= 16) return true;
    if (x >= MAP - 17 && x <= MAP - 5 && z >= MAP - 17 && z <= MAP - 5) return true;
    return false;
  }

  private flattenHeightPads(): void {
    for (let z = 0; z < MAP; z++) {
      for (let x = 0; x < MAP; x++) {
        if (this.isHeightPad(x, z)) this.height[x + z * MAP] = 0;
      }
    }
  }

  /** 2-tile ring outside pads — clamp steep rim to level 1 so exits stay walkable. */
  private skirtRampsAroundPads(): void {
    const rect = (cx: number, cz: number, halfW: number, halfD: number) => ({
      x0: Math.floor(cx - halfW),
      x1: Math.ceil(cx + halfW) - 1,
      z0: Math.floor(cz - halfD),
      z1: Math.ceil(cz + halfD) - 1,
    });
    const regions = [
      rect(OPENING_CENTER.x, OPENING_CENTER.z, OPENING_CORRIDOR.halfW, OPENING_CORRIDOR.halfD),
      rect(
        OPENING_CENTER.x,
        OPENING_CENTER.z - OPENING_CAMP.offset,
        OPENING_CAMP.halfW,
        OPENING_CAMP.halfD,
      ),
      rect(
        OPENING_CENTER.x,
        OPENING_CENTER.z + OPENING_CAMP.offset,
        OPENING_CAMP.halfW,
        OPENING_CAMP.halfD,
      ),
      { x0: 4, x1: 16, z0: 4, z1: 16 },
      { x0: MAP - 17, x1: MAP - 5, z0: MAP - 17, z1: MAP - 5 },
    ];
    for (const r of regions) {
      const x0 = Math.max(0, r.x0 - 2);
      const x1 = Math.min(MAP - 1, r.x1 + 2);
      const z0 = Math.max(0, r.z0 - 2);
      const z1 = Math.min(MAP - 1, r.z1 + 2);
      for (let z = z0; z <= z1; z++) {
        for (let x = x0; x <= x1; x++) {
          if (x >= r.x0 && x <= r.x1 && z >= r.z0 && z <= r.z1) continue;
          const i = x + z * MAP;
          if (this.height[i] >= 2) this.height[i] = 1;
        }
      }
    }
  }

  private cliffableTile(tile: number): boolean {
    return tile !== Tile.Void;
  }

  private markCliffTile(x: number, z: number): void {
    if (this.isHeightPad(x, z)) return;
    const i = x + z * MAP;
    if (!this.cliffableTile(this.tiles[i])) return;
    this.tiles[i] = Tile.Rock;
    this.block[i] = 1;
  }

  /** Steep ±2 level steps become blocking cliff rock on the high tile (ramps stay walkable dust). */
  private applyHeightCliffs(): void {
    for (let z = 1; z < MAP - 1; z++) {
      for (let x = 1; x < MAP - 1; x++) {
        const i = x + z * MAP;
        if (!this.cliffableTile(this.tiles[i])) continue;
        const hi = this.height[i];
        for (let k = 0; k < 4; k++) {
          const nx = x + DX[k];
          const nz = z + DZ[k];
          if (nx < 0 || nz < 0 || nx >= MAP || nz >= MAP) continue;
          const ni = nx + nz * MAP;
          const nh = this.height[ni];
          if (Math.abs(hi - nh) < 2) continue;
          if (hi > nh) this.markCliffTile(x, z);
          else if (nh > hi) this.markCliffTile(nx, nz);
        }
      }
    }
  }

  /** Dust pad + interior rocks + gem nodes + props under the opening camera frustum. */
  private stampOpeningGround(): void {
    const cx = OPENING_CENTER.x;
    const cz = OPENING_CENTER.z;
    const paintRect = (x0: number, x1: number, z0: number, z1: number): void => {
      for (let z = z0; z <= z1; z++) {
        for (let x = x0; x <= x1; x++) {
          if (x < 1 || z < 1 || x >= MAP - 1 || z >= MAP - 1) continue;
          this.tiles[x + z * MAP] = Tile.Dust;
          this.block[x + z * MAP] = 0;
        }
      }
    };
    paintRect(
      Math.floor(cx - OPENING_CORRIDOR.halfW),
      Math.ceil(cx + OPENING_CORRIDOR.halfW) - 1,
      Math.floor(cz - OPENING_CORRIDOR.halfD),
      Math.ceil(cz + OPENING_CORRIDOR.halfD) - 1,
    );
    for (const sign of [-1, 1]) {
      const campZ = cz + sign * OPENING_CAMP.offset;
      paintRect(
        Math.floor(cx - OPENING_CAMP.halfW),
        Math.ceil(cx + OPENING_CAMP.halfW) - 1,
        Math.floor(campZ - OPENING_CAMP.halfD),
        Math.ceil(campZ + OPENING_CAMP.halfD) - 1,
      );
    }
    // Gems — ore among Helion workers, gas at +Z camp, solar east of north camp.
    this.placeOpeningNodeAt(Tile.Ore, cx - 0.25, cz - 6.45);
    this.placeOpeningNodeAt(Tile.Gas, cx + 1.1, cz + 6.45);
    this.placeOpeningNodeAt(Tile.Solar, cx + 1.1, cz - 6.45);
    // Contested mid ore plus scenery kept outside the combat corridor.
    this.placeOpeningNodeAt(Tile.Ore, cx, cz);
    for (const prop of OPENING_CONVOY) this.placePropAt(Tile.PropWreck, cx + prop.x, cz + prop.z);
    for (const prop of OPENING_VENTS) this.placePropAt(Tile.PropVent, cx + prop.x, cz + prop.z);
  }

  /** One ground tile + gem entity at exact world coords. */
  private placeOpeningNodeAt(kind: Tile, x: number, z: number): void {
    const cx = x | 0;
    const cz = z | 0;
    if (cx < 1 || cz < 1 || cx >= MAP - 1 || cz >= MAP - 1) return;
    this.tiles[cx + cz * MAP] = kind;
    this.block[cx + cz * MAP] = 0;
    const node = this.spawn(Kind.Resource, 'vespari', 3, x, z);
    if (node) {
      node.cargoType = kind;
      node.hp = kind === Tile.Ore ? OPENING_ORE_RESERVE : kind === Tile.Gas ? 200 : 160;
      node.maxHp = node.hp;
      node.radius = 0.55;
    }
  }

  /** Prop billboard at exact world coords (duel-lane boulders). */
  private placePropAt(kind: Tile, x: number, z: number): void {
    if (x < 1 || z < 1 || x >= MAP - 1 || z >= MAP - 1) return;
    const prop = this.spawn(Kind.Resource, 'vespari', 3, x, z);
    if (prop) {
      prop.cargoType = kind;
      prop.hp = 9999;
      prop.maxHp = 9999;
      prop.radius = 0.45;
    }
  }

  private placePatch(kind: Tile, cx: number, cz: number, r: number): void {
    const r2 = r * r;
    for (let z = -3; z <= 3; z++) {
      for (let x = -3; x <= 3; x++) {
        if (x * x + z * z > r2) continue;
        const xx = cx + x;
        const zz = cz + z;
        if (xx < 1 || zz < 1 || xx >= MAP - 1 || zz >= MAP - 1) continue;
        this.tiles[xx + zz * MAP] = kind;
        this.block[xx + zz * MAP] = 0;
      }
    }
    const node = this.spawn(Kind.Resource, 'vespari', 3, cx + 0.5, cz + 0.5);
    if (node) {
      node.cargoType = kind;
      node.hp = kind === Tile.Ore ? 280 : kind === Tile.Gas ? 200 : 160;
      node.maxHp = node.hp;
    }
  }

  private openingClashEnt(e: Ent): boolean {
    if (!e.alive || e.team > 1) return false;
    if (e.kind !== Kind.Fighter && e.kind !== Kind.Ravager && e.kind !== Kind.Prism) return false;
    const dx = e.x - OPENING_CENTER.x;
    const dz = e.z - OPENING_CENTER.z;
    return dx * dx + dz * dz < 110;
  }

  private openingFlankCampEnt(e: Ent): boolean {
    if (!e.alive) return false;
    if (!inOpeningCamp(e.x, e.z)) return false;
    if (e.kind === Kind.House || e.kind === Kind.Worker) return true;
    if (e.kind !== Kind.Resource) return false;
    return e.cargoType !== Tile.PropWreck && e.cargoType !== Tile.PropVent;
  }

  private openingMidGemWorkerEnt(e: Ent): boolean {
    if (!e.alive || e.kind !== Kind.Worker) return false;
    return Math.abs(e.tx - OPENING_CENTER.x) < 0.15 && Math.abs(e.tz - OPENING_CENTER.z) < 0.15;
  }

  private openingTableauWorker(e: Ent): boolean {
    return this.openingFlankCampEnt(e) || this.openingMidGemWorkerEnt(e);
  }

  private strikeRange(e: Ent, st: (typeof STATS)[number], t: Ent): number {
    let r = st.range + t.radius;
    if (!st.melee) r += pathEffects(this.teams[e.team].techPath).rangedRangeBonus;
    if (this.tick < 240 && this.openingClashEnt(e) && !st.melee) r += 0.95;
    return r;
  }

  private openingDmgMul(attacker: Ent, target: Ent): number {
    if (this.tick >= 240 || !this.openingClashEnt(attacker)) return 1;
    if (target.team === 1) {
      if (this.tick < 50) return 1.15;
      return 0.15;
    }
    if (target.team === 0) return 0.35;
    return 1;
  }

  private stampPatch(kind: Tile, count: number, rng: () => number): void {
    for (let i = 0; i < count; i++) {
      const cx = 8 + (rng() * (MAP - 16)) | 0;
      const cz = 8 + (rng() * (MAP - 16)) | 0;
      if ((cx < 18 && cz < 18) || (cx > MAP - 18 && cz > MAP - 18)) continue;
      const mdx = cx + 0.5 - MAP * 0.5;
      const mdz = cz + 0.5 - MAP * 0.52;
      if (mdx * mdx + mdz * mdz < 16 * 16) continue;
      const r = kind === Tile.Ore ? 2 : 1.6;
      this.placePatch(kind, cx, cz, r);
    }
  }

  private clearBase(cx: number, cz: number): void {
    for (let z = -6; z <= 6; z++) {
      for (let x = -6; x <= 6; x++) {
        const xx = cx + x;
        const zz = cz + z;
        if (xx < 0 || zz < 0 || xx >= MAP || zz >= MAP) continue;
        this.tiles[xx + zz * MAP] = Tile.Dust;
        this.block[xx + zz * MAP] = 0;
      }
    }
  }

  private spawnScenario(): void {
    // M2-A opening — six entities per side, mirrored through the map center.
    const workerOffsets: readonly (readonly [number, number])[] = [
      [-2.6, -2.2],
      [2.6, -2.2],
      [-2.6, 2.6],
      [2.6, 2.6],
    ];
    const nodeOffsets: readonly (readonly [Tile, number, number])[] = [
      [Tile.Ore, 5.5, -1.5],
      [Tile.Gas, -1.5, 5.5],
      [Tile.Solar, 5.5, 4.5],
    ];
    for (let t = 0; t < 2; t++) {
      const sgn = t === 0 ? 1 : -1;
      const bx = t === 0 ? 10.5 : MAP - 10.5;
      const bz = t === 0 ? 10.5 : MAP - 10.5;
      this.spawn(Kind.Hall, this.civ[t], t, bx, bz);
      for (const [dx, dz] of workerOffsets) {
        this.spawn(Kind.Worker, this.civ[t], t, bx + dx * sgn, bz + dz * sgn);
      }
      this.spawn(Kind.Scout, this.civ[t], t, bx + 4.2 * sgn, bz - 4.2 * sgn);
      // Safe mirrored gems — one of each type within reach of every Core.
      for (const [kind, dx, dz] of nodeOffsets) {
        this.placeOpeningNodeAt(kind, bx + dx * sgn, bz + dz * sgn);
      }
    }
  }

  private thinkUnits(): void {
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.hp <= 0 || isBuilding(e.kind) || e.kind === Kind.Resource) continue;
      const st = STATS[e.kind];
      e.cooldown = Math.max(0, e.cooldown - DT);
      e.blinkCd = Math.max(0, e.blinkCd - DT);
      if (e.hitFlash > 0) e.hitFlash = Math.max(0, e.hitFlash - DT * 2.25);
      if (e.combatT > 0) e.combatT = Math.max(0, e.combatT - DT);
      e.anim += DT;
      if (e.frenzy > 0) e.frenzy = Math.max(0, e.frenzy - DT * 0.15);

      if (e.kind === Kind.Shade && e.order === Ord.Idle) e.stealth = Math.min(1, e.stealth + DT * 0.6);
      if (e.order === Ord.Attack || e.order === Ord.AttackMove) e.stealth = 0;

      let target: Ent | null = e.tid >= 0 ? this.ents[e.tid] : null;
      if (target && (!target.alive || target.hp <= 0)) {
        target = null;
        e.tid = -1;
      }

      const holdFire =
        this.tick < 240 && this.openingClashEnt(e) && e.order === Ord.Attack;

      if (e.order === Ord.Attack || e.order === Ord.AttackMove || e.order === Ord.Idle) {
        if (!target) target = this.acquire(e, st.los + 1.5);
        if (target) {
          e.tid = target.id;
          const d = Math.sqrt(dist2(e.x, e.z, target.x, target.z));
          const clashZ = MAP * 0.52;
          const crossedCenter = e.team === 0 ? e.z >= clashZ : e.z <= clashZ;
          const marchThrough =
            this.tick < 90 &&
            e.order === Ord.AttackMove &&
            this.openingClashEnt(e) &&
            !st.melee &&
            !crossedCenter;
          if (d <= this.strikeRange(e, st, target)) {
            this.tryStrike(e, target, st);
            if (holdFire || !marchThrough) {
              e.vx = e.vz = 0;
              e.path = null;
              if (holdFire) e.facing = dir8(target.x - e.x, target.z - e.z);
              continue;
            }
          }
          if (e.order === Ord.Idle && d < st.los) {
            e.order = Ord.Attack;
            e.tx = target.x;
            e.tz = target.z;
          }
        }
      }

      if (holdFire) {
        e.vx = e.vz = 0;
        e.path = null;
        continue;
      }

      if (e.order === Ord.Gather || e.order === Ord.Return) this.thinkGather(e);
      else if (e.order === Ord.Build) this.thinkBuild(e);
      else if (e.order === Ord.Move || e.order === Ord.Attack || e.order === Ord.AttackMove) {
        const clashZ = MAP * 0.52;
        const crossedCenter = e.team === 0 ? e.z >= clashZ : e.z <= clashZ;
        const openingMarch =
          this.tick < 90 &&
          e.order === Ord.AttackMove &&
          this.openingClashEnt(e) &&
          !crossedCenter;
        const gx = target && e.order !== Ord.Move && !openingMarch ? target.x : e.tx;
        const gz = target && e.order !== Ord.Move && !openingMarch ? target.z : e.tz;
        // M4 decision 4 — sky-dominion quickens non-worker combat movement.
        const spdMul =
          e.kind === Kind.Worker ? 1 : pathEffects(this.teams[e.team].techPath).combatSpeedMul;
        this.steer(e, gx, gz, st.spd * spdMul * (1 + e.frenzy * 0.08));
        const pathDone = !e.path || e.pathI >= (e.path.length >> 1);
        const reachedGoalTile = pathDone && tileAt(e.x, e.z) === tileAt(e.tx, e.tz);
        if (e.order === Ord.Move && (dist2(e.x, e.z, e.tx, e.tz) < 0.16 || reachedGoalTile)) {
          e.order = Ord.Idle;
          e.vx = e.vz = 0;
          e.path = null;
        }
      } else {
        e.vx *= 0.7;
        e.vz *= 0.7;
      }

      if (e.kind === Kind.Shade && e.hp < e.maxHp * 0.35 && e.blinkCd <= 0 && target) {
        const ang = Math.atan2(e.z - target.z, e.x - target.x);
        e.x += Math.cos(ang) * 3.2;
        e.z += Math.sin(ang) * 3.2;
        e.x = clamp(e.x, 1, MAP - 1);
        e.z = clamp(e.z, 1, MAP - 1);
        e.blinkCd = 8;
        e.stealth = 1;
      }
    }
  }

  private thinkGather(e: Ent): void {
    const st = STATS[e.kind];
    const openingCampWorker =
      this.tick < 240 && e.kind === Kind.Worker && this.openingTableauWorker(e);
    if (!openingCampWorker && (e.cargo >= GATHER_MAX || e.order === Ord.Return)) {
      const hall = this.nearestHall(e.team, e.x, e.z);
      if (!hall) {
        e.order = Ord.Idle;
        return;
      }
      e.order = Ord.Return;
      if (dist2(e.x, e.z, hall.x, hall.z) < (hall.radius + 0.55) ** 2) {
        const eco = this.teams[e.team];
        if (e.cargoType === Tile.Ore) {
          eco.ore += e.cargo;
          this.recordGathered(e.team, 'ore', e.cargo);
        } else if (e.cargoType === Tile.Gas) {
          eco.gas += e.cargo;
          this.recordGathered(e.team, 'gas', e.cargo);
        } else {
          eco.energy += e.cargo;
          this.recordGathered(e.team, 'energy', e.cargo);
        }
        e.cargo = 0;
        e.order = Ord.Gather;
      } else this.steer(e, hall.x, hall.z, st.spd);
      return;
    }
    let node = e.tid >= 0 ? this.ents[e.tid] : null;
    // M2-D R2 — team 1 gathers only from nodes it has discovered.
    if (!node?.alive || node.kind !== Kind.Resource || (e.team === 1 && (node.seenBy & SEEN_RIVAL) === 0)) {
      node =
        e.team === 1 ? this.nearestResourceForTeam(1, e.x, e.z) : this.nearestResource(e.x, e.z);
    }
    if (!node) {
      e.order = Ord.Idle;
      return;
    }
    e.tid = node.id;
    const gatherR = openingCampWorker
      ? this.openingMidGemWorkerEnt(e)
        ? 2.8
        : 2.05
      : node.radius + 0.45;
    if (dist2(e.x, e.z, node.x, node.z) < gatherR * gatherR) {
      e.vx = e.vz = 0;
      e.path = null;
      if (e.cooldown <= 0) {
        // M3-A — Sunweaver workers pulse energy over a Solar link instead of hauling.
        if (this.trySolarLinkPulse(e, node)) return;
        e.cargo += 1;
        e.cargoType = node.cargoType;
        node.hp -= 1;
        e.cooldown = 0.55;
        if (node.hp <= 0) this.kill(node);
      }
    } else if (!openingCampWorker) this.steer(e, node.x, node.z, st.spd);
    else {
      e.vx = e.vz = 0;
      e.path = null;
    }
  }

  /** M3-A — try a linked Solar energy pulse for a Sunweaver worker (docs/M3_ECONOMIES.md A1–A3). */
  private trySolarLinkPulse(e: Ent, node: Ent): boolean {
    if (e.civ !== 'vespari' || node.cargoType !== Tile.Solar) return false;
    const hall = this.nearestHall(e.team, node.x, node.z);
    if (!hall) return false;
    let link = this.links.find((l) => l.nodeId === node.id && l.team === e.team);
    if (!link) {
      link = { nodeId: node.id, hallId: hall.id, team: e.team, severedUntil: -1 };
      this.links.push(link);
    }
    if (link.severedUntil > this.tick) return false;
    // A3 — an enemy unit near the tether midpoint severs the link for 10 s.
    const mx = (node.x + hall.x) * 0.5;
    const mz = (node.z + hall.z) * 0.5;
    const r2 = 1.8 * 1.8;
    for (let i = 0; i < MAX_ENTS; i++) {
      const u = this.ents[i];
      if (!u.alive || u.team === e.team) continue;
      if (!isUnit(u.kind)) continue;
      if (dist2(u.x, u.z, mx, mz) < r2) {
        const fx = pathEffects(this.teams[e.team].techPath);
        link.severedUntil = this.tick + Math.round(LINK_SEVER_TICKS * fx.linkSeverScale);
        return false;
      }
    }
    const eco = this.teams[e.team];
    eco.energy += 1;
    this.recordGathered(e.team, 'energy', 1);
    node.hp -= 1;
    e.cooldown = 0.4;
    if (node.hp <= 0) {
      this.kill(node);
      const idx = this.links.indexOf(link);
      if (idx >= 0) this.links.splice(idx, 1);
    }
    return true;
  }

  private thinkBuild(e: Ent): void {
    const st = STATS[e.kind];
    const b = e.tid >= 0 ? this.ents[e.tid] : null;
    if (!b?.alive) {
      e.order = Ord.Idle;
      return;
    }
    // M3-B — Gravemark workers raise extraction rigs on ore/gas nodes.
    if (b.kind === Kind.Resource) {
      if (e.civ !== 'aurion') {
        e.order = Ord.Idle;
        return;
      }
      if (b.cargoType !== Tile.Ore && b.cargoType !== Tile.Gas) {
        e.order = Ord.Idle;
        return;
      }
      if (b.rigTeam >= 0 && b.rigTeam !== e.team) {
        e.order = Ord.Idle;
        return;
      }
      if (dist2(e.x, e.z, b.x, b.z) < (b.radius + 0.5) ** 2) {
        e.vx = e.vz = 0;
        if (b.rigTeam < 0) b.rigTeam = e.team;
        b.rigProgress = Math.min(1, b.rigProgress + DT * 0.1);
        const fx = pathEffects(this.teams[e.team].techPath);
        b.rigHp = 700 * b.rigProgress * fx.rigHpMul;
        if (b.rigProgress >= 1) e.order = Ord.Gather;
      } else this.steer(e, b.x, b.z, st.spd);
      return;
    }
    if (dist2(e.x, e.z, b.x, b.z) < (b.radius + 0.5) ** 2) {
      e.vx = e.vz = 0;
      b.progress = Math.min(1, b.progress + DT * 0.12);
      b.hp = Math.min(b.maxHp, b.maxHp * b.progress);
      if (b.progress >= 1) e.order = Ord.Idle;
    } else this.steer(e, b.x, b.z, st.spd);
  }

  /** M3-B B3 — finished rigs auto-extract 1 unit per 1.0 s for 0.5 node hp. */
  private stepRigs(): void {
    for (let i = 0; i < MAX_ENTS; i++) {
      const n = this.ents[i];
      if (!n.alive || n.kind !== Kind.Resource) continue;
      if (n.rigTeam < 0 || n.rigProgress < 1) continue;
      if (n.cargoType !== Tile.Ore && n.cargoType !== Tile.Gas) continue;
      const eco = this.teams[n.rigTeam];
      const iv = pathEffects(eco.techPath).rigExtractSec;
      n.rigAccum += DT;
      while (n.rigAccum >= iv && n.hp > 0) {
        n.rigAccum -= iv;
        n.hp = Math.max(0, n.hp - 0.5);
        if (n.cargoType === Tile.Ore) {
          eco.ore += 1;
          this.recordGathered(n.rigTeam, 'ore', 1);
        } else {
          eco.gas += 1;
          this.recordGathered(n.rigTeam, 'gas', 1);
        }
      }
      if (n.hp <= 0) this.kill(n);
    }
  }

  /** M3-C C2/C3 — drains active boosts and auto-disables them below 4 energy. */
  private stepBoosts(): void {
    const DRAIN = [0, 8, 5, 6];
    for (let t = 0; t < 2; t++) {
      const kind = this.boosts[t];
      if (kind === 0) continue;
      const eco = this.teams[t];
      if (eco.energy <= 4) {
        this.boosts[t] = 0;
        continue;
      }
      const fx = pathEffects(eco.techPath);
      eco.energy = Math.max(0, eco.energy - DRAIN[kind] * DT * fx.boostDrainMul);
      if (eco.energy < 4) this.boosts[t] = 0;
    }
  }

  /** M4 — resolves the commit channel: applies the pending path once, irreversibly. */
  private stepAge(): void {
    for (let t = 0; t < 2; t++) {
      const eco = this.teams[t];
      if (eco.ageT <= 0) continue;
      eco.ageT -= DT;
      if (eco.ageT <= 0) {
        eco.ageT = 0;
        const pending = this.pendingPath[t];
        this.pendingPath[t] = null;
        if (pending !== null && eco.techPath === null) {
          // Decision 5 — epoch=1 written once for legacy readers, then never again.
          eco.techPath = pending;
          eco.epoch = 1;
        } else if (eco.epoch === 0) {
          eco.epoch = 1;
        }
      }
    }
  }

  private thinkBuildings(): void {
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || !isBuilding(e.kind)) continue;
      if (e.progress < 1) continue;
      if (e.kind === Kind.Hall && e.civ === 'vespari') {
        // Slow self-repair.
        e.hp = Math.min(e.maxHp, e.hp + DT * 2);
      }
      // M3-C — Shields boost regen for this team's finished buildings.
      if (this.boosts[e.team] === 3) {
        e.hp = Math.min(e.maxHp, e.hp + DT * 6);
      }
      if (e.kind === Kind.UniqueB && e.civ === 'vespari') {
        this.sporeT[e.team] += DT;
        if (this.sporeT[e.team] > 28) {
          this.sporeT[e.team] = 0;
          const eco = this.teams[e.team];
          if (eco.pop < eco.cap) {
            const w = this.spawn(Kind.Worker, e.civ, e.team, e.rallyX, e.rallyZ);
            if (w) {
              w.order = Ord.Gather;
            }
          }
        }
      }
      if (e.kind === Kind.UniqueB && e.civ === 'aurion' && e.cooldown <= 0) {
        const t = this.acquireTurret(e, STATS[Kind.UniqueB].range);
        if (t) {
          this.spawnBolt(e, t, STATS[Kind.UniqueB].atk, 9);
          e.cooldown = 1.1;
        }
      } else e.cooldown = Math.max(0, e.cooldown - DT);

      if (e.trainT > 0) {
        const spd = (e.civ === 'vespari' ? 1.2 : 1) * (this.boosts[e.team] === 1 ? 1.8 : 1);
        e.trainT -= DT * spd;
        if (e.trainT <= 0) {
          e.trainT = 0;
          const u = this.spawn(e.trainKind, e.civ, e.team, e.rallyX, e.rallyZ);
          if (u) {
            if (
              this.scriptedMarshalEnabled &&
              e.team === 1 &&
              this.tick >= 240 &&
              isUnit(u.kind) &&
              u.kind !== Kind.Worker
            ) {
              u.order = Ord.Idle;
              this.queueMarshalPeel(u);
            } else if (e.team === 1 && this.tick < AI_ATTACK_EARLIEST_TICK) {
              this.orderAiAttackMoveCenter(u);
            } else {
              u.order = Ord.AttackMove;
              u.tx = e.rallyX + (e.team === 0 ? 1 : -1);
              u.tz = e.rallyZ;
            }
          }
          this.recountPop();
        }
      }
    }
  }

  /** M3-B B4 — a finished rig absorbs damage before the base node does. */
  private damageRigAware(t: Ent, dmg: number, attackerTeam = -1): void {
    const hpBefore = t.hp;
    if (t.kind === Kind.Resource && t.rigTeam >= 0 && t.rigProgress >= 1) {
      t.rigHp -= dmg;
      if (t.rigHp <= 0) {
        t.rigHp = 0;
        t.rigTeam = -1;
        t.rigProgress = 0;
        t.rigAccum = 0;
        this.spawnSpark(t.x, t.z, 1, 'aurion');
      }
      return;
    }
    t.hp -= dmg;
    if (
      t.kind === Kind.Hall &&
      this.matchStatsTracking &&
      (attackerTeam === 0 || attackerTeam === 1) &&
      (t.team === 0 || t.team === 1) &&
      t.team !== attackerTeam
    ) {
      const removed = Math.max(0, Math.min(Math.max(0, hpBefore), hpBefore - t.hp));
      this.matchStatsData[attackerTeam].coreDamage += removed;
    }
  }

  private tryStrike(e: Ent, t: Ent, st: typeof STATS[number]): void {
    if (e.cooldown > 0) return;
    if (this.tick < 240 && t.kind === Kind.Worker) return;
    const dmg = st.atk * (1 + e.frenzy * 0.12) * (e.civ === 'aurion' && isBuilding(e.kind) === false ? 1 : 1);
    const bonus = t.civ === 'aurion' ? 0.85 : 1; // compact armor
    const applied =
      dmg *
      (isBuilding(t.kind) && e.kind === Kind.Prism ? 1.8 : 1) *
      (e.civ === 'aurion' ? 0.92 : 1) *
      this.openingDmgMul(e, t);
    if (st.melee) {
      this.damageRigAware(t, applied * bonus, e.team);
      if (t.team === 1 && this.tick < 240) t.hitFlash = 0.45;
      this.spawnSpark(t.x, t.z, 1, e.civ);
      this.onHit?.();
      this.markCombat(e, t);
      e.cooldown = e.kind === Kind.Ravager ? 0.72 : 0.85;
      if (e.kind === Kind.Ravager && t.hp <= 0) e.frenzy = Math.min(6, e.frenzy + 1);
    } else {
      const opening = this.tick < 240 && this.openingClashEnt(e);
      const spd = opening ? 6.0 : e.kind === Kind.Prism ? 11 : e.kind === Kind.Siege ? 6.5 : 8.5;
      const life = opening ? 1.4 : 1.1;
      this.spawnBolt(e, t, applied * bonus, spd, life);
      this.markCombat(e, t);
      e.cooldown = opening ? 0.32 : e.kind === Kind.Prism ? 1.15 : 0.95;
    }
    if (t.hp <= 0) this.kill(t);
  }

  private spawnBolt(e: Ent, t: Ent, dmg: number, spd: number, life = 1.1): void {
    const dx = t.x - e.x;
    const dz = t.z - e.z;
    const d = Math.hypot(dx, dz) || 1;
    this.bolts.push({
      x: e.x,
      z: e.z,
      vx: (dx / d) * spd,
      vz: (dz / d) * spd,
      team: e.team,
      civ: e.civ,
      dmg,
      life,
      kind: e.kind,
    });
    this.spawnSpark(e.x, e.z, 0, e.civ);
    this.onMuzzle?.();
  }

  private spawnSpark(x: number, z: number, kind: number, civ: Civ): void {
    const s = this.sparks[this.sparkHead];
    this.sparkHead = (this.sparkHead + 1) % MAX_SPARKS;
    s.x = x;
    s.z = z;
    s.kind = kind;
    s.civ = civ;
    s.maxLife = kind === 0 ? 0.1 : 0.12;
    s.life = s.maxLife;
    s.active = true;
  }

  private stepSparks(): void {
    for (let i = 0; i < MAX_SPARKS; i++) {
      const s = this.sparks[i];
      if (!s.active) continue;
      s.life -= DT;
      if (s.life <= 0) s.active = false;
    }
  }

  private stepCorpses(): void {
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive) continue;
      if (e.dissolveT > 0) {
        e.dissolveT -= DT;
        if (e.dissolveT < 0) e.dissolveT = 0;
        continue;
      }
      if (e.corpseT <= 0) continue;
      e.corpseT -= DT;
      if (e.corpseT <= 0) {
        e.alive = false;
        e.dissolveT = 0;
        this.free.push(e.id);
      }
    }
  }

  private stepBolts(): void {
    for (let i = this.bolts.length - 1; i >= 0; i--) {
      const b = this.bolts[i];
      b.x += b.vx * DT;
      b.z += b.vz * DT;
      b.life -= DT;
      let hit = false;
      this.hash.query(b.x, b.z, 0.7, this.q);
      for (const id of this.q) {
        const e = this.ents[id];
        if (!e.alive || e.hp <= 0 || e.team === b.team) continue;
        // M3-B — bolts may strike rigged resource nodes; plain nodes stay immune.
        if (e.kind === Kind.Resource && e.rigTeam < 0) continue;
        if (e.kind === Kind.Worker) continue;
        if (e.kind === Kind.Shade && e.stealth > 0.6) continue;
        if (dist2(b.x, b.z, e.x, e.z) < (e.radius + 0.25) ** 2) {
          this.damageRigAware(e, b.dmg, b.team);
          if (e.team === 1 && this.tick < 240) e.hitFlash = 0.45;
          this.markCombat(e);
          this.spawnSpark(b.x, b.z, 1, b.civ);
          this.onHit?.();
          hit = true;
          if (e.hp <= 0) this.kill(e);
          break;
        }
      }
      if (hit || b.life <= 0 || b.x < 0 || b.z < 0 || b.x > MAP || b.z > MAP) {
        this.bolts.splice(i, 1);
      }
    }
  }

  private steer(e: Ent, gx: number, gz: number, spd: number): void {
    const startX = e.x;
    const startZ = e.z;
    if (!e.path || e.pathI >= (e.path.length >> 1)) {
      e.path = this.pathfind(e.x, e.z, gx, gz);
      e.pathI = 0;
    }
    let tx = gx;
    let tz = gz;
    if (e.path && e.pathI < e.path.length >> 1) {
      tx = e.path[e.pathI * 2] + 0.5;
      tz = e.path[e.pathI * 2 + 1] + 0.5;
      if (dist2(e.x, e.z, tx, tz) < 0.35) e.pathI++;
    }
    const dx = tx - e.x;
    const dz = tz - e.z;
    const d = Math.hypot(dx, dz) || 1;
    e.vx = (dx / d) * spd;
    e.vz = (dz / d) * spd;
    const nx = e.x + e.vx * DT;
    const nz = e.z + e.vz * DT;
    if (!this.blockedWorld(nx, nz)) {
      e.x = nx;
      e.z = nz;
    } else if (!this.blockedWorld(nx, e.z)) e.x = nx;
    else if (!this.blockedWorld(e.x, nz)) e.z = nz;
    else {
      e.path = this.pathfind(e.x, e.z, gx, gz);
      e.pathI = 0;
      e.vx = e.vz = 0;
    }
    if (Math.abs(e.x - startX) + Math.abs(e.z - startZ) < 0.0001) e.vx = e.vz = 0;
    e.x = clamp(e.x, 0.6, MAP - 0.6);
    e.z = clamp(e.z, 0.6, MAP - 0.6);
    if (e.kind === Kind.Worker) {
      if (Math.abs(e.vx) + Math.abs(e.vz) > 0.05) e.facing = dir8(e.vx, e.vz);
    } else if (Math.abs(e.vx) + Math.abs(e.vz) > 0.05) {
      e.facing = dir8(e.vx, e.vz);
    }
  }

  private moveSeparate(): void {
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || !isUnit(e.kind)) continue;
      if (this.tick < 240 && this.openingClashEnt(e) && e.order === Ord.Attack) continue;
      if (this.tick < 240 && e.kind === Kind.Worker && this.openingTableauWorker(e)) continue;
      this.hash.query(e.x, e.z, 1.1, this.q);
      let sx = 0;
      let sz = 0;
      for (const id of this.q) {
        if (id === i) continue;
        const o = this.ents[id];
        if (!o.alive || !isUnit(o.kind)) continue;
        const d2 = dist2(e.x, e.z, o.x, o.z);
        const min = e.radius + o.radius;
        if (d2 > 0.0001 && d2 < min * min) {
          const d = Math.sqrt(d2);
          const p = (min - d) / d;
          sx += (e.x - o.x) * p;
          sz += (e.z - o.z) * p;
        }
      }
      e.x += sx * 0.28;
      e.z += sz * 0.28;
    }
  }

  private acquire(e: Ent, los: number): Ent | null {
    let best: Ent | null = null;
    let bestD = los * los;
    this.hash.query(e.x, e.z, los, this.q);
    for (const id of this.q) {
      const o = this.ents[id];
      if (!o.alive || o.hp <= 0 || o.team === e.team) continue;
      if (o.kind === Kind.Resource) continue;
      if (e.team === 1 && o.team === 0 && o.kind === Kind.Hall && (o.seenBy & SEEN_RIVAL) === 0) continue;
      if (o.kind === Kind.Shade && o.stealth > 0.55) continue;
      const d = dist2(e.x, e.z, o.x, o.z);
      if (d < bestD) {
        bestD = d;
        best = o;
      }
    }
    return best;
  }

  private acquireTurret(e: Ent, los: number): Ent | null {
    return this.acquire(e, los);
  }

  private nearestHall(team: number, x: number, z: number): Ent | null {
    let best: Ent | null = null;
    let bestD = 1e9;
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.team !== team || e.kind !== Kind.Hall || e.progress < 1) continue;
      const d = dist2(x, z, e.x, e.z);
      if (d < bestD) {
        bestD = d;
        best = e;
      }
    }
    return best;
  }

  /** M2-D — AI-visible resource search: only nodes this team has discovered (R2). */
  nearestResourceForTeam(team: number, x: number, z: number): Ent | null {
    const bit = team === 0 ? SEEN_PLAYER : SEEN_RIVAL;
    let best: Ent | null = null;
    let bestD = 1e9;
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.kind !== Kind.Resource) continue;
      if (e.cargoType !== Tile.Ore && e.cargoType !== Tile.Gas && e.cargoType !== Tile.Solar) continue;
      if ((e.seenBy & bit) === 0) continue;
      const d = dist2(x, z, e.x, e.z);
      if (d < bestD) {
        bestD = d;
        best = e;
      }
    }
    return best;
  }

  private nearestResource(x: number, z: number): Ent | null {
    let best: Ent | null = null;
    let bestD = 1e9;
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.kind !== Kind.Resource) continue;
      if (e.cargoType !== Tile.Ore && e.cargoType !== Tile.Gas && e.cargoType !== Tile.Solar) continue;
      const d = dist2(x, z, e.x, e.z);
      if (d < bestD) {
        bestD = d;
        best = e;
      }
    }
    return best;
  }

  pathfind(sx: number, sz: number, gx: number, gz: number): number[] | null {
    const s = tileAt(sx, sz);
    const g = tileAt(gx, gz);
    if (s === g) return [g % MAP, (g / MAP) | 0];
    const stamp = this.stamp++;
    if (stamp > 65000) {
      this.closed.fill(0);
      this.stamp = 1;
    }
    const heap = this.heap;
    heap.clear();
    this.gScore[s] = 0;
    this.came[s] = -1;
    this.closed[s] = stamp;
    heap.push(0, s);
    let found = -1;
    let n = 0;
    while (heap.size && n++ < 1800) {
      const cur = heap.pop();
      if (cur === g) {
        found = cur;
        break;
      }
      const cx = cur % MAP;
      const cz = (cur / MAP) | 0;
      for (let k = 0; k < 8; k++) {
        const nx = cx + DX[k];
        const nz = cz + DZ[k];
        if (nx < 0 || nz < 0 || nx >= MAP || nz >= MAP) continue;
        const ni = nx + nz * MAP;
        if (this.block[ni] && ni !== g) continue;
        const ng = this.gScore[cur] + DC[k];
        if (this.closed[ni] === stamp && ng >= this.gScore[ni]) continue;
        this.closed[ni] = stamp;
        this.gScore[ni] = ng;
        this.came[ni] = cur;
        const h = Math.abs(nx - (g % MAP)) + Math.abs(nz - ((g / MAP) | 0));
        heap.push(ng + h, ni);
      }
    }
    if (found < 0) return null;
    const path: number[] = [];
    let c = found;
    while (c >= 0 && path.length < 160) {
      path.push(c % MAP, (c / MAP) | 0);
      c = this.came[c];
    }
    const out: number[] = [];
    for (let i = path.length - 2; i >= 0; i -= 2) {
      out.push(path[i], path[i + 1]);
    }
    return out.length ? out : null;
  }

  private blockedWorld(x: number, z: number): boolean {
    return !!this.block[tileAt(x, z)];
  }

  private discoveryBit(team: number): number {
    return team === 0 ? SEEN_PLAYER : SEEN_RIVAL;
  }

  private entityDiscoveryMeta(e: Ent): Pick<EntityDiscoveryEvent, 'kind' | 'label'> {
    if (e.kind === Kind.Resource) {
      const label =
        e.cargoType === Tile.Ore
          ? 'Ore Field'
          : e.cargoType === Tile.Gas
            ? 'Volatile Field'
            : e.cargoType === Tile.Solar
              ? 'Lumen Field'
              : e.cargoType === Tile.PropWreck
                ? 'Neutral Tech Relic'
                : 'Rift Vent';
      return { kind: 'resource', label };
    }
    if (isBuilding(e.kind)) {
      return { kind: 'enemy-structure', label: e.kind === Kind.Hall ? 'Enemy Core' : 'Enemy Structure' };
    }
    return { kind: 'entity', label: 'Enemy Unit' };
  }

  private markEntityDiscovered(team: number, e: Ent): void {
    const bit = this.discoveryBit(team);
    if ((e.seenBy & bit) !== 0) return;
    e.seenBy |= bit;
    if (e.team === team) return;
    const meta = this.entityDiscoveryMeta(e);
    const event: EntityDiscoveryEvent = {
      team,
      tick: this.tick,
      id: e.id,
      kind: meta.kind,
      label: meta.label,
      x: e.x,
      z: e.z,
    };
    this.discoveryLog.push(event);
    this.onDiscover?.(event);
  }

  private markLandmarksDiscovered(team: number): void {
    const bit = this.discoveryBit(team);
    const visible = this.visible[team];
    for (const landmark of this.landmarks) {
      if ((landmark.discoveredBy & bit) !== 0) continue;
      if (!visible[tileAt(landmark.x, landmark.z)]) continue;
      landmark.discoveredBy |= bit;
      const event: DiscoveryEvent = {
        team,
        tick: this.tick,
        id: landmark.id,
        kind: landmark.kind,
        label: landmark.label,
        x: landmark.x,
        z: landmark.z,
      };
      this.discoveryLog.push(event);
      this.onDiscover?.(event);
    }
  }

  private markVisibleEntitiesDiscovered(team: number): void {
    const visible = this.visible[team];
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive) continue;
      if (e.team !== team && e.kind === Kind.Shade && e.stealth > 0.55) continue;
      if (e.team === team || visible[tileAt(e.x, e.z)]) this.markEntityDiscovered(team, e);
    }
  }

  private resetLumen(): void {
    this.lumenOwner = -1;
    this.lumenCapturing = -1;
    this.lumenProgress = 0;
    this.lumenContested = false;
    this.lumenPulseRemaining[0] = 0;
    this.lumenPulseRemaining[1] = 0;
    this.lumenChargeAccum[0] = 0;
    this.lumenChargeAccum[1] = 0;
    this.lumenOwnedAccum[0] = 0;
    this.lumenOwnedAccum[1] = 0;
  }

  /** VS-2B — deterministic Central Lumen capture, income, and pulse accounting. */
  private stepLumen(): void {
    for (let team = 0; team < 2; team++) {
      this.lumenPulseRemaining[team] = Math.max(0, this.lumenPulseRemaining[team] - DT);
    }

    let playerPresent = false;
    let rivalPresent = false;
    const radius2 = LUMEN_CAPTURE_RADIUS * LUMEN_CAPTURE_RADIUS;
    const landmark = this.landmarks.find((entry) => entry.id === 'central-lumen-field');
    if (landmark) {
      for (let i = 0; i < MAX_ENTS; i++) {
        const e = this.ents[i];
        if (
          !e.alive ||
          e.hp <= 0 ||
          (e.kind !== Kind.Fighter && e.kind !== Kind.Ravager && e.kind !== Kind.Prism)
        ) continue;
        if (dist2(e.x, e.z, landmark.x, landmark.z) > radius2) continue;
        if (e.team === 0) playerPresent = true;
        else if (e.team === 1) rivalPresent = true;
      }
    }

    const bothPresent = playerPresent && rivalPresent;
    this.lumenContested = bothPresent;
    if (!playerPresent && !rivalPresent) {
      this.lumenCapturing = -1;
      this.lumenProgress = 0;
    } else if (bothPresent) {
      this.lumenCapturing = -1;
      this.lumenProgress = 0;
    } else {
      const team: 0 | 1 = playerPresent ? 0 : 1;
      if (this.lumenOwner === team) {
        this.lumenCapturing = -1;
        this.lumenProgress = 0;
      } else {
        if (this.lumenCapturing !== team) {
          this.lumenCapturing = team;
          this.lumenProgress = 0;
        }
        this.lumenProgress = Math.min(LUMEN_CAPTURE_SECONDS, this.lumenProgress + DT);
        if (this.lumenProgress + 1e-9 >= LUMEN_CAPTURE_SECONDS) {
          this.lumenOwner = team;
          this.lumenCapturing = -1;
          this.lumenProgress = 0;
          this.lumenChargeAccum[team] = 0;
          this.lumenOwnedAccum[team] = 0;
        }
      }
    }

    if (this.lumenOwner !== 0 && this.lumenOwner !== 1) return;
    const owner: 0 | 1 = this.lumenOwner;
    const eco = this.teams[owner];
    this.lumenChargeAccum[owner] += DT;
    while (this.lumenChargeAccum[owner] + 1e-9 >= 1) {
      this.lumenChargeAccum[owner] -= 1;
      eco.energy += 1;
      this.recordGathered(owner, 'energy', 1);
    }
    this.lumenOwnedAccum[owner] += DT;
    while (this.lumenOwnedAccum[owner] + 1e-9 >= LUMEN_PULSE_INTERVAL) {
      this.lumenOwnedAccum[owner] -= LUMEN_PULSE_INTERVAL;
      this.lumenPulseRemaining[owner] = LUMEN_PULSE_SECONDS;
    }
  }

  private updateFog(): void {
    if (!this.fogOfWarEnabled) {
      this.visible[0].fill(1);
      this.visible[1].fill(1);
      this.explored[0].fill(1);
      this.explored[1].fill(1);
      for (let team = 0; team < 2; team++) {
        this.markVisibleEntitiesDiscovered(team);
        this.markLandmarksDiscovered(team);
      }
      for (let i = 0; i < MAX_ENTS; i++) {
        if (this.ents[i].alive) this.ents[i].vis = true;
      }
      return;
    }
    this.visible[0].fill(0);
    this.visible[1].fill(0);
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.team > 1) continue;
      const los =
        STATS[e.kind].los +
        (e.civ === 'voidmarked' ? 1 : 0) +
        (this.boosts[e.team] === 2 ? 2.5 : 0) +
        (e.kind === Kind.Scout ? pathEffects(this.teams[e.team].techPath).scoutLosBonus : 0);
      const r = Math.ceil(los);
      const vis = this.visible[e.team];
      const exp = this.explored[e.team];
      const cx = e.x | 0;
      const cz = e.z | 0;
      const los2 = los * los;
      for (let z = cz - r; z <= cz + r; z++) {
        if (z < 0 || z >= MAP) continue;
        for (let x = cx - r; x <= cx + r; x++) {
          if (x < 0 || x >= MAP) continue;
          const dx = x + 0.5 - e.x;
          const dz = z + 0.5 - e.z;
          if (dx * dx + dz * dz <= los2) {
            const idx = x + z * MAP;
            vis[idx] = 1;
            exp[idx] = 1;
          }
        }
      }
    }
    for (let team = 0; team < 2; team++) {
      if (this.lumenPulseRemaining[team] <= 0) continue;
      this.visible[team].fill(1);
      this.explored[team].fill(1);
    }
    for (let team = 0; team < 2; team++) {
      this.markVisibleEntitiesDiscovered(team);
      this.markLandmarksDiscovered(team);
    }
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive) continue;
      if (e.team === 0) {
        e.vis = true;
        continue;
      }
      if (e.kind === Kind.Resource) {
        e.vis = (e.seenBy & SEEN_PLAYER) !== 0;
        continue;
      }
      const idx = tileAt(e.x, e.z);
      e.vis = this.visible[0][idx] === 1 && !(e.kind === Kind.Shade && e.stealth > 0.55);
    }
  }

  private stepFlags(): void {
    for (let i = this.flags.length - 1; i >= 0; i--) {
      this.flags[i].t -= DT;
      if (this.flags[i].t <= 0) this.flags.splice(i, 1);
    }
  }

  private orderAttackMoveHall(e: Ent, hall: Ent): void {
    e.order = Ord.AttackMove;
    e.tx = hall.x;
    e.tz = hall.z;
    e.tid = hall.id;
    e.path = null;
    e.pathI = 0;
  }

  private isRaidingPlayerHall(e: Ent): boolean {
    if (e.tid < 0) return false;
    const t = this.ents[e.tid];
    return !!t?.alive && t.team === 0 && t.kind === Kind.Hall && t.hp > 0;
  }

  private shouldMarshalPeel(e: Ent): boolean {
    if (!e.alive || e.team !== 1 || !isUnit(e.kind) || e.kind === Kind.Worker) return false;
    if (e.kind === Kind.Siege || e.kind === Kind.Shade) return false;
    if (this.isRaidingPlayerHall(e)) return false;
    if (this.openingClashEnt(e)) return true;
    if (e.order === Ord.Idle) return true;
    if (e.order === Ord.AttackMove) return true;
    if (e.order === Ord.Attack) {
      const t = e.tid >= 0 ? this.ents[e.tid] : null;
      return !t || !t.alive || t.hp <= 0;
    }
    return false;
  }

  private buildMarshalPeelQueue(): void {
    const q: number[] = [];
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!this.shouldMarshalPeel(e)) continue;
      q.push(e.id);
    }
    q.sort((a, b) => a - b);
    this.marshalPeelQ = q;
    this.marshalPeelI = 0;
    this.marshalPeelBuilt = true;
  }

  private queueMarshalPeel(e: Ent): void {
    if (!this.shouldMarshalPeel(e)) return;
    if (!this.marshalPeelQ.includes(e.id)) this.marshalPeelQ.push(e.id);
  }

  /** Tick 240: off-screen doctrine commit for the enemy marshal; ore/gas/charge for mixed-arms Yard queue. */
  private stepEnemyMarshal(): void {
    if (this.tick < 240) return;
    const eco = this.teams[1];
    eco.ore = Math.max(eco.ore, 120);
    eco.gas = Math.max(eco.gas, 40);
    eco.energy = Math.max(eco.energy, 40);
    if (this.tick === 240) {
      // M4 decision 6 — instant doctrine commit replaces the old epoch jump.
      eco.techPath = this.civ[1] === 'vespari' ? 'sky-dominion' : 'iron-colossus';
      this.pendingPath[1] = null;
      eco.ageT = 0;
      eco.ore = Math.max(eco.ore, 500);
      eco.gas = Math.max(eco.gas, 120);
      eco.energy = Math.max(eco.energy, 160);
      for (let i = 0; i < MAX_ENTS; i++) {
        const e = this.ents[i];
        if (!e.alive || e.team !== 1) continue;
        if (e.kind === Kind.Barracks) {
          e.rallyX = MARSHAL_FORWARD_X;
          e.rallyZ = MARSHAL_FORWARD_Z;
          if (e.trainKind === Kind.Scout) {
            e.trainT = 0;
            e.trainKind = Kind.Worker;
          }
        }
        if (e.kind === Kind.Hall && e.trainKind === Kind.Scout) {
          e.trainT = 0;
          e.trainKind = Kind.Worker;
        }
      }
    }
    this.pumpMarshalTraining();
  }

  private pumpMarshalTraining(): void {
    const eco = this.teams[1];
    if (!gateOpen(eco, Kind.Fighter)) return;
    let hall: Ent | null = null;
    let barracks: Ent | null = null;
    let workers = 0;
    let fighters = 0;
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.team !== 1) continue;
      if (e.kind === Kind.Hall) hall = e;
      if (e.kind === Kind.Barracks) barracks = e;
      if (e.kind === Kind.Worker) workers++;
      if (e.kind === Kind.Fighter && e.hp > 0) fighters++;
    }
    if (!barracks) return;
    const stF = STATS[Kind.Fighter];
    const room = eco.pop + stF.pop <= eco.cap;
    const pay = eco.ore >= stF.ore && eco.energy >= stF.energy;
    if (fighters >= 8 || !room || !pay) return;
    if (barracks.trainT <= 0) this.tryMarshalTrain(barracks, Kind.Fighter);
    if (hall && hall.trainT <= 0 && workers >= 8 && eco.ageT <= 0) {
      this.tryMarshalTrain(hall, Kind.Fighter);
    }
  }

  private tryMarshalTrain(building: Ent, kind: Kind): boolean {
    if (!this.tryTrain(building, kind)) return false;
    if (building.team === 1 && this.tick >= 240) {
      if (kind === Kind.Fighter) {
        building.trainT = Math.min(building.trainT, MARSHAL_FIGHTER_TRAIN);
      }
    }
    return true;
  }

  /** After opening clash, stagger enemy military toward the player Nexus. */
  private stepMarshalPeel(): void {
    if (this.tick < 240) return;
    const playerHall = this.nearestHall(0, 10.5, 10.5);
    if (!playerHall) return;
    if (!this.marshalPeelBuilt) this.buildMarshalPeelQueue();
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.team !== 1 || !isUnit(e.kind) || e.kind === Kind.Worker) continue;
      this.queueMarshalPeel(e);
    }
    while (this.marshalPeelI < this.marshalPeelQ.length) {
      const peelAt = 240 + this.marshalPeelI * MARSHAL_PEEL_STAGGER_TICKS;
      if (this.tick < peelAt) break;
      const id = this.marshalPeelQ[this.marshalPeelI++];
      const e = this.ents[id];
      if (e.alive && this.shouldMarshalPeel(e)) this.orderAttackMoveHall(e, playerHall);
    }
  }

  private stepAi(): void {
    this.aiT += DT;
    if (this.aiT + 1e-9 < AI_CADENCE_SECONDS[this.aiDifficulty]) return;
    this.aiT = 0;
    const eco = this.teams[1];
    let hall: Ent | null = null;
    let yard: Ent | null = null;
    let workers = 0;
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.team !== 1) continue;
      if (e.kind === Kind.Hall) hall = e;
      if (e.kind === Kind.Barracks) yard = e;
      if (e.kind === Kind.Worker) workers++;
    }

    // VS-2A — the only way the rival creates its Yard is the public placement seam.
    if (!yard && hall && hall.progress >= 1 && hall.hp > 0) {
      this.tryAiPlaceYard(hall);
      yard = this.ents.find((e) => e.alive && e.team === 1 && e.kind === Kind.Barracks) ?? null;
    }

    // The opening four Workers plus one Scout need one honest House for a 2+2 force.
    if (
      hall &&
      yard &&
      !this.ents.some((e) => e.alive && e.team === 1 && e.kind === Kind.House)
    ) {
      this.tryAiPlaceHouse(hall);
    }

    // VS5 — replace a lost rival Scout through the ordinary Hall training seam until Core discovery.
    const playerHall = this.ents.find(
      (e) => e.alive && e.hp > 0 && e.team === 0 && e.kind === Kind.Hall && e.progress >= 1,
    );
    const rivalScoutAlive = this.ents.some(
      (e) => e.alive && e.hp > 0 && e.team === 1 && e.kind === Kind.Scout,
    );
    const scoutStats = STATS[Kind.Scout];
    if (
      hall &&
      playerHall &&
      (playerHall.seenBy & SEEN_RIVAL) === 0 &&
      !rivalScoutAlive &&
      hall.progress >= 1 &&
      hall.hp > 0 &&
      hall.trainT <= 0 &&
      eco.ageT <= 0 &&
      eco.pop + scoutStats.pop <= eco.cap &&
      eco.ore >= scoutStats.ore &&
      eco.gas >= scoutStats.gas &&
      eco.energy >= scoutStats.energy
    ) {
      this.tryTrain(hall, Kind.Scout);
    }

    // Keep the opening Worker count stable so the population cap can hold the compact army.
    if (hall && hall.trainT <= 0 && workers < 4 && eco.ore >= STATS[Kind.Worker].ore && eco.pop < eco.cap) {
      this.tryTrain(hall, Kind.Worker);
    }

    if (yard && yard.progress >= 1) {
      this.tryAiCommitPath(hall, yard);
      this.tryAiTrainMix(yard);
    }

    // Idle Workers return to the normal gather loop after construction decisions.
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (e.alive && e.team === 1 && e.kind === Kind.Worker && e.order === Ord.Idle) e.order = Ord.Gather;
    }

    // M2-D R3/R4 — deterministic scouting and sight-based target invalidation.
    this.stepAiScout();
    this.stepAiTargetInvalidation();
    // M3-B B5 — Gravemark AI assigns idle workers to raise rigs on discovered nodes.
    this.stepAiRigs();
    this.stepAiFieldOrders();
    // M3-C C3 — Sunweaver AI boost policy: production while training, vision while scouting.
    if (this.civ[1] === 'vespari') {
      const trainingMilitary =
        (yard !== null && yard.trainT > 0) || (hall !== null && hall.trainT > 0);
      if (trainingMilitary && eco.energy > 80) this.boosts[1] = 1;
      else if (this.tick < 1800 && eco.energy > 30) this.boosts[1] = 2;
      else this.boosts[1] = 0;
    }
  }

  /** VS-2A — choose an unloaded Worker, preferring Idle then Gather, without interrupting Build. */
  private aiBuilderWorker(): Ent | null {
    let idle: Ent | null = null;
    let gatherer: Ent | null = null;
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.team !== 1 || e.kind !== Kind.Worker || e.cargo !== 0 || e.order === Ord.Build) continue;
      if (e.order === Ord.Idle) {
        idle ??= e;
        if (!this.aiNearestResourceIsSolar(e)) return e;
      }
      if (e.order === Ord.Gather && gatherer === null) gatherer = e;
    }
    return idle ?? gatherer;
  }

  private aiNearestResourceIsSolar(e: Ent): boolean {
    const target = e.tid >= 0 ? this.ents[e.tid] : null;
    const node = target?.alive && target.kind === Kind.Resource
      ? target
      : this.nearestResourceForTeam(1, e.x, e.z);
    return node?.cargoType === Tile.Solar;
  }

  /** VS-2A — one legal placement from a frozen ring; tryPlace is called only after canPlace. */
  private tryAiPlaceFromRing(
    kind: Kind,
    center: { x: number; z: number },
    offsets: readonly { dx: number; dz: number }[],
  ): boolean {
    const st = STATS[kind];
    const eco = this.teams[1];
    if (eco.ore < st.ore || eco.gas < st.gas || eco.energy < st.energy) return false;
    const builder = this.aiBuilderWorker();
    if (!builder) return false;
    for (const offset of offsets) {
      const x = center.x + offset.dx;
      const z = center.z + offset.dz;
      if (!this.canPlace(x, z, st.radius)) continue;
      return this.tryPlace(1, kind, x, z, builder.id);
    }
    return false;
  }

  /** VS-2A — one Yard maximum, built by a real Worker near the completed Core. */
  private tryAiPlaceYard(hall: Ent): boolean {
    if (this.ents.some((e) => e.alive && e.team === 1 && e.kind === Kind.Barracks)) return false;
    return this.tryAiPlaceFromRing(Kind.Barracks, hall, AI_YARD_RING_OFFSETS);
  }

  /** VS-2A — the existing cap needs one normal House for the compact 2+2 force. */
  private tryAiPlaceHouse(hall: Ent): boolean {
    const unique = this.civ[1] === 'vespari' ? Kind.Ravager : Kind.Prism;
    const desiredArmyPop = STATS[Kind.Fighter].pop * 2 + STATS[unique].pop * 2;
    if (this.teams[1].pop + desiredArmyPop <= this.teams[1].cap) return false;
    if (this.ents.some((e) => e.alive && e.team === 1 && e.kind === Kind.House)) return false;
    return this.tryAiPlaceFromRing(Kind.House, hall, AI_HOUSE_RING_OFFSETS);
  }

  /** VS-2A — legal path entry; the normal 40-second channel owns the eventual write. */
  private tryAiCommitPath(hall: Ent | null, yard: Ent): boolean {
    const eco = this.teams[1];
    if (
      this.tick < AI_PATH_EARLIEST_TICK ||
      !hall ||
      hall.progress < 1 ||
      hall.hp <= 0 ||
      hall.trainT > 0 ||
      yard.progress < 1 ||
      eco.ageT > 0 ||
      eco.techPath !== null ||
      eco.epoch !== 0 ||
      eco.ore < PATH_COMMIT_ORE ||
      eco.energy < PATH_COMMIT_CHARGE
    ) {
      return false;
    }
    const path: TechPathId = this.civ[1] === 'vespari' ? 'sky-dominion' : 'iron-colossus';
    return this.tryCommitPath(1, path);
  }

  private aiCountKindAndQueued(kind: Kind): number {
    let count = 0;
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.team !== 1) continue;
      if (e.kind === kind && e.hp > 0) count++;
      if (e.trainT > 0 && e.trainKind === kind) count++;
    }
    return count;
  }

  private aiCanTrain(yard: Ent, kind: Kind): boolean {
    const st = STATS[kind];
    const eco = this.teams[1];
    return yard.alive && yard.progress >= 1 && yard.trainT <= 0 && gateOpen(eco, kind)
      && eco.ore >= st.ore && eco.gas >= st.gas && eco.energy >= st.energy
      && eco.pop + st.pop <= eco.cap;
  }

  /** VS-2A — lower living+queued side first, with a legal affordability fallback. */
  private tryAiTrainMix(yard: Ent): boolean {
    if (this.teams[1].ageT > 0) return false;
    const unique = this.civ[1] === 'vespari' ? Kind.Ravager : Kind.Prism;
    const fighters = this.aiCountKindAndQueued(Kind.Fighter);
    const uniques = this.aiCountKindAndQueued(unique);
    const preferred = fighters <= uniques ? Kind.Fighter : unique;
    const fallback = preferred === Kind.Fighter ? unique : Kind.Fighter;
    if (this.aiCanTrain(yard, preferred)) return this.tryTrain(yard, preferred);
    if (this.aiCanTrain(yard, fallback)) return this.tryTrain(yard, fallback);
    return false;
  }

  /** VS-2A-R2 — keep a combat-ready field force on the shared center before the attack floor. */
  private orderAiAttackMoveCenter(e: Ent): void {
    e.order = Ord.AttackMove;
    e.tx = OPENING_CENTER.x;
    e.tz = OPENING_CENTER.z;
    e.tid = -1;
    e.path = this.pathfind(e.x, e.z, OPENING_CENTER.x, OPENING_CENTER.z);
    e.pathI = 0;
  }

  /** VS-2A — rally the field at center until a discovered Core and four-unit force exist. */
  private stepAiFieldOrders(): void {
    const hall = this.ents.find((e) => e.alive && e.team === 0 && e.kind === Kind.Hall && e.hp > 0);
    const field = this.ents.filter(
      (e) => e.alive && e.hp > 0 && e.team === 1
        && (e.kind === Kind.Fighter || e.kind === Kind.Ravager || e.kind === Kind.Prism),
    );
    const coreDiscovered = hall !== undefined && (hall.seenBy & SEEN_RIVAL) !== 0;
    if (hall && coreDiscovered && field.length >= 4 && this.tick >= AI_ATTACK_EARLIEST_TICK) {
      for (const unit of field) {
        if (unit.order !== Ord.AttackMove || unit.tid !== hall.id) this.orderAttackMoveHall(unit, hall);
      }
      return;
    }
    for (const unit of field) {
      if (dist2(unit.x, unit.z, OPENING_CENTER.x, OPENING_CENTER.z) < 0.28 * 0.28) {
        this.orderAiAttackMoveCenter(unit);
      } else if (
        unit.order !== Ord.AttackMove ||
        unit.tid !== -1 ||
        Math.abs(unit.tx - OPENING_CENTER.x) > 0.01 ||
        Math.abs(unit.tz - OPENING_CENTER.z) > 0.01
      ) {
        this.orderAiAttackMoveCenter(unit);
      }
    }
  }

  /** M3-B B5 — aurion AI: one idle worker per discovered unrigged ore/gas node, capped. */
  private stepAiRigs(): void {
    if (this.civ[1] !== 'aurion') return;
    let builders = 0;
    const claimed = new Set<number>();
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.team !== 1 || e.kind !== Kind.Worker) continue;
      if (e.order === Ord.Build && e.tid >= 0) {
        const t = this.ents[e.tid];
        if (t?.alive && t.kind === Kind.Resource) {
          builders++;
          claimed.add(e.tid);
        }
      }
    }
    if (builders >= 2) return;
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.team !== 1 || e.kind !== Kind.Worker) continue;
      // The census above turns idle workers into Gather; rigs recruit unloaded
      // gatherers (not hauling cargo) and never touch the opening tableau.
      const eligible =
        e.order === Ord.Gather && e.cargo === 0 && !this.openingTableauWorker(e);
      if (!eligible) continue;
      // Keep one ordinary discovered-resource gatherer on Solar so Charge for
      // the legal path and normal Yard training is earned, never granted.
      if (this.aiNearestResourceIsSolar(e)) continue;
      if (builders >= 2) return;
      const node = this.nearestUnriggedNode(e.x, e.z, claimed);
      if (!node) return;
      claimed.add(node.id);
      e.order = Ord.Build;
      e.tid = node.id;
      e.tx = node.x;
      e.tz = node.z;
      e.path = null;
      e.pathI = 0;
      builders++;
    }
  }

  /** Nearest discovered ore/gas node without a friendly rig and not already claimed. */
  private nearestUnriggedNode(x: number, z: number, claimed: Set<number>): Ent | null {
    let best: Ent | null = null;
    let bestD = 1e9;
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.kind !== Kind.Resource) continue;
      if (e.cargoType !== Tile.Ore && e.cargoType !== Tile.Gas) continue;
      if ((e.seenBy & SEEN_RIVAL) === 0) continue;
      if (e.rigTeam >= 0) continue;
      if (claimed.has(e.id)) continue;
      const d = dist2(x, z, e.x, e.z);
      if (d < bestD) {
        bestD = d;
        best = e;
      }
    }
    return best;
  }

  /** VS-2A — send an idle rival Scout to the nearest deterministic unexplored frontier tile. */
  private stepAiScout(): void {
    const scout = this.ents.find(
      (e) => e.alive && e.team === 1 && e.kind === Kind.Scout && e.order === Ord.Idle,
    );
    if (!scout) return;
    const exp = this.explored[1];
    let bestX = -1;
    let bestZ = -1;
    let bestTravel = Infinity;
    let bestProgress = Infinity;
    let bestIndex = Infinity;
    for (let z = 0; z < MAP; z++) {
      for (let x = 0; x < MAP; x++) {
        const idx = x + z * MAP;
        if (exp[idx] !== 0 || this.block[idx] !== 0) continue;
        let frontier = false;
        for (let dz = -1; dz <= 1 && !frontier; dz++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dz === 0) continue;
            const xx = x + dx;
            const zz = z + dz;
            if (xx < 0 || zz < 0 || xx >= MAP || zz >= MAP) continue;
            if (exp[xx + zz * MAP] !== 0) {
              frontier = true;
              break;
            }
          }
        }
        if (!frontier) continue;
        const travel = dist2(scout.x, scout.z, x + 0.5, z + 0.5);
        const progress = dist2(x + 0.5, z + 0.5, 10.5, 10.5);
        if (
          travel < bestTravel ||
          (travel === bestTravel && (progress < bestProgress || (progress === bestProgress && idx < bestIndex)))
        ) {
          bestTravel = travel;
          bestProgress = progress;
          bestIndex = idx;
          bestX = x;
          bestZ = z;
        }
      }
    }
    if (bestX < 0) return;
    scout.order = Ord.Move;
    scout.tx = bestX + 0.5;
    scout.tz = bestZ + 0.5;
    scout.tid = -1;
    scout.path = null;
    scout.pathI = 0;
  }

  /** M2-D R4 — attackers drop targets their team can no longer see. */
  private stepAiTargetInvalidation(): void {
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.team !== 1 || e.order !== Ord.Attack || e.tid < 0) continue;
      const t = this.ents[e.tid];
      if (!t.alive || t.hp <= 0) continue;
      if (t.team === 1) continue;
      if (this.visible[1][tileAt(t.x, t.z)] !== 0) continue;
      e.order = Ord.Idle;
      e.tid = -1;
      e.vx = 0;
      e.vz = 0;
      e.path = null;
      e.pathI = 0;
    }
  }

  recountPop(): void {
    for (const t of this.teams) {
      t.pop = 0;
      t.cap = 0;
    }
    for (let i = 0; i < MAX_ENTS; i++) {
      const e = this.ents[i];
      if (!e.alive || e.team > 1) continue;
      if (e.hp <= 0 || e.corpseT > 0 || e.dissolveT > 0) continue;
      const st = STATS[e.kind];
      this.teams[e.team].pop += st.pop;
      if (e.kind === Kind.Hall && e.progress >= 1) this.teams[e.team].cap += POP_HALL;
      if (e.kind === Kind.House && e.progress >= 1) this.teams[e.team].cap += POP_HOUSE;
    }
  }
}
